import type { ActionField, FieldMeta } from "@wms/domain";
import { taxIdLabel } from "./kzRequisites";

/**
 * ТЗ «Рабочие столы и персонализация» (31.10.2026), раздел 13 (UXW-60…66) и приложение §5 (IN01…IN11): the input
 * guard. A pure policy built from the real meta of a field judges the WHOLE value an edit would produce — a key, a
 * paste, a drop, an autofill — and either accepts it or refuses it with a short reason. A refused edit leaves the
 * previous value as it was: nothing is clamped (100 never becomes 10), cut (13 digits never become 12), stripped of
 * letters or transliterated. Intermediate states on the way to a valid value pass («», «1,», «-» where a sign is
 * allowed), an IME composition is judged only when it ends. Codes, barcodes and GS1 strings keep their own handling:
 * no mask is applied to them. The server validates every request exactly as before (UXW-65).
 */

export type InputKind = "free" | "text" | "number" | "tax_id" | "iik" | "bic" | "kbe" | "phone";

export interface InputPolicy {
  kind: InputKind;
  /** text: the most characters the column keeps; only a paste beyond it is refused (typing is explained by the rule). */
  maxLength?: number | null;
  /** number: a whole number (an integer column). */
  integer?: boolean;
  /** number: digits after the comma — the scale of the column or the precision of the unit, the smaller one. */
  decimals?: number | null;
  /** number: the unit the precision comes from («шт», «кг») — the words of the reason. */
  unit?: string | null;
  /** number: a minus is allowed (no lower bound at or above zero). */
  signed?: boolean;
  /** number: the hard maximum the server checks; `maxExclusive` — the bound itself is not allowed. */
  max?: number | null;
  maxExclusive?: boolean;
  /** tax_id: the words of the field («БИН», «ИИН», «БИН/ИИН»); `spaces` — spaces between the digits are kept. */
  label?: string;
  spaces?: boolean;
}

export type Verdict = { ok: true } | { ok: false; reason: string };

export interface EditInfo {
  /** An IME composition is going on: nothing is judged until it ends (IN04). */
  composing?: boolean;
  /** The edit inserts more than one character at once (a paste, a drop, an autofill). */
  paste?: boolean;
}

const OK: Verdict = { ok: true };
const no = (reason: string): Verdict => ({ ok: false, reason });

/** Fields that hold a scanned or external code: their strings stay exactly as read (IN09, UXW-62). */
const CODE_NAMES = /(^|_)(barcode|barcodes|gtin|sscc|ean|gs1|raw|serial|serials|code|codes|number|article|sku|external_id|erp_number)(_|$)/;

/** Fields the server checks as a phone (`common/validators.normalize_phone`, applied to the column `phone`). */
const PHONE_NAMES = new Set(["phone"]);

function plural(n: number, one: string, few: string, many: string): string {
  const tail = n % 10;
  const tail2 = n % 100;
  if (tail === 1 && tail2 !== 11) return one;
  if (tail >= 2 && tail <= 4 && (tail2 < 12 || tail2 > 14)) return few;
  return many;
}

const digitsWord = (n: number) => `${n} ${plural(n, "цифра", "цифры", "цифр")}`;
const signsWord = (n: number) => `${n} ${plural(n, "знака", "знаков", "знаков")}`;

/** «3,5» → "3.5"; "" when there are no digits yet. */
function numberText(candidate: string): string {
  return candidate.replace(",", ".");
}

function judgeNumber(p: InputPolicy, candidate: string): Verdict {
  if (/\s/.test(candidate)) return no("Число вводится без пробелов");
  if (/[eE]/.test(candidate)) return no("Здесь число: буквы не вводятся");
  if (/[^0-9.,-]/.test(candidate)) return no(/[A-Za-zА-Яа-яЁёӘәҒғҚқҢңӨөҰұҮүҺһІі]/.test(candidate) ? "Здесь число: буквы не вводятся" : "Здесь число: только цифры и запятая");
  if (candidate.includes("-")) {
    if (!p.signed) return no("Отрицательное число здесь не допускается");
    if (candidate.lastIndexOf("-") !== 0) return no("Минус ставится только в начале числа");
  }
  const separators = (candidate.match(/[.,]/g) ?? []).length;
  if (separators > 1) return no("В числе одна запятая");
  const [, frac = ""] = numberText(candidate).replace("-", "").split(".");
  const wantsFraction = separators === 1;
  const decimals = p.integer ? 0 : (p.decimals ?? null);
  if (decimals !== null && wantsFraction && (decimals === 0 || frac.length > decimals)) {
    if (decimals === 0) {
      if (p.unit === "шт") return no("Для штук количество — целое число");
      if (p.unit) return no(`Для «${p.unit}» количество — целое число`);
      return no("Здесь целое число");
    }
    const tail = `не больше ${signsWord(decimals)} после запятой`;
    return no(p.unit ? `Для «${p.unit}» можно ${tail}` : `Можно ${tail}`);
  }
  // the hard maximum: the whole value is refused, never clamped (IN07); an unfinished «1,» is judged as 1
  if (p.max !== null && p.max !== undefined) {
    const value = Number(numberText(candidate).replace(/\.$/, ""));
    if (!Number.isNaN(value) && /\d/.test(candidate) && (p.maxExclusive ? value >= p.max : value > p.max)) {
      const shown = String(p.max).replace(".", ",");
      return no(p.maxExclusive ? `Должно быть меньше ${shown}: «${candidate}» не принято` : `Максимум ${shown}: «${candidate}» не принято`);
    }
  }
  return OK;
}

function judgeTaxId(p: InputPolicy, candidate: string, edit: EditInfo): Verdict {
  const label = p.label ?? "БИН/ИИН";
  const body = p.spaces ? candidate.replace(/ /g, "") : candidate;
  if (!/^\d*$/.test(body)) {
    return no(edit.paste ? `Вставка не принята: в ${label} только цифры — букв и знаков нет` : `${label}: только цифры — буквы и знаки не вводятся`);
  }
  if (body.length > 12) {
    return no(edit.paste ? `Вставка не принята: в ${label} 12 цифр, а во вставке ${body.length}` : `${label} — 12 цифр: ${body.length}-я цифра не добавлена`);
  }
  return OK;
}

function judgeCompact(candidate: string, alphabet: RegExp, max: number, alphabetWords: string, lengthWords: string): Verdict {
  if (!alphabet.test(candidate)) return no(alphabetWords);
  if (candidate.replace(/\s/g, "").length > max) return no(lengthWords);
  return OK;
}

/** The verdict on the value an edit would leave in the field. */
export function judge(policy: InputPolicy, candidate: string, edit: EditInfo = {}): Verdict {
  if (edit.composing || candidate === "") return OK;
  switch (policy.kind) {
    case "number":
      return judgeNumber(policy, candidate);
    case "tax_id":
      return judgeTaxId(policy, candidate, edit);
    case "iik":
      return judgeCompact(candidate, /^[0-9A-Za-z\s]*$/, 20, "ИИК: латинские буквы и цифры, например KZ…", "ИИК — 20 знаков: KZ и 18 цифр или букв");
    case "bic":
      return judgeCompact(candidate, /^[0-9A-Za-z\s]*$/, 11, "БИК: латинские буквы и цифры, например HSBKKZKX", "БИК — 8 знаков (11 — с кодом филиала)");
    case "kbe":
      return judgeCompact(candidate, /^\d*$/, 2, "КБе: только цифры, например 17", "КБе — 2 цифры");
    case "phone": {
      if (!/^[0-9+()\-\s]*$/.test(candidate)) return no("Телефон: цифры, «+», скобки и дефис, например +7 701 000 00 00");
      const digits = candidate.replace(/\D/g, "").length;
      return digits > 15 ? no(`Телефон — не больше 15 цифр (сейчас ${digitsWord(digits)})`) : OK;
    }
    case "text":
      // UX3010-04 stays: typing past the limit is explained by the rule under the field; a too long paste is refused
      // whole instead of being cut by the browser (UXW-62)
      if (edit.paste && policy.maxLength && candidate.length > policy.maxLength) {
        return no(`Вставка не принята: не больше ${policy.maxLength} символов, а получилось бы ${candidate.length}`);
      }
      return OK;
    default:
      return OK;
  }
}

/** The kind of an edit from the native event: a paste, a drop or a replacement inserts more than one character. */
export function isBulk(inputType: string | undefined, data: string | null | undefined): boolean {
  if (inputType === "insertFromPaste" || inputType === "insertFromDrop" || inputType === "insertReplacementText" || inputType === "insertFromYank") return true;
  return (data ?? "").length > 1;
}

/** The value after an insert at the selection: the candidate the policy judges (UXW-62: the whole value, not the key). */
export function candidateOf(value: string, start: number | null, end: number | null, inserted: string): string {
  const from = start ?? value.length;
  const to = end ?? from;
  return value.slice(0, from) + inserted + value.slice(to);
}

/**
 * The form value of a number typed as text: a complete number in the plain form of the API («1,5» → "1.5",
 * «,5» → "0.5", «007» → "7"); an unfinished one («1,», «-») is kept as typed with a dot, so the rule under the field
 * can ask to finish it; empty — undefined (the field is cleared).
 */
export function numberValue(text: string): string | undefined {
  if (text === "") return undefined;
  const plain = numberText(text);
  const m = /^(-?)(\d*)(?:\.(\d*))?$/.exec(plain);
  if (!m) return plain;
  const [, sign, whole, frac] = m;
  if (!whole && !frac) return plain;
  if (frac === "") return plain; // «1,» — unfinished
  const intPart = (whole || "0").replace(/^0+(?=\d)/, "");
  return `${sign}${intPart}${frac ? `.${frac}` : ""}`;
}

/** The rule of the form: an unfinished number is not a value (UXW-63: the final check on blur/submit). */
export function unfinishedNumber(value: unknown): string | null {
  if (typeof value !== "string" || value === "") return null;
  return /^-?\d+(\.\d+)?$/.test(value) ? null : "Допишите число, например 1,25";
}

/** How a stored or computed number is shown in the field: «1,25», never «1.250». */
export function numberDisplay(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  const text = String(value);
  return /^-?\d+(\.\d+)?$/.test(text) ? text.replace(".", ",") : text;
}

/**
 * The policy of a field of a meta form (UXW-60): by its type, length, numeric contract and `check`; codes, comments,
 * JSON and the rest keep their existing handling (null — not guarded).
 */
export function policyOfField(field: Pick<FieldMeta, "name" | "type" | "max_length" | "check" | "integer" | "scale" | "min" | "max" | "max_exclusive" | "width">, unit?: { name: string; decimals: number } | null): InputPolicy | null {
  if (field.type === "number") {
    const scale = field.scale ?? null;
    const unitDecimals = unit ? unit.decimals : null;
    const decimals = scale === null ? unitDecimals : unitDecimals === null ? scale : Math.min(scale, unitDecimals);
    return {
      kind: "number",
      integer: !!field.integer,
      decimals,
      unit: unit && unitDecimals !== null && (scale === null || unitDecimals <= scale) ? unit.name : null,
      signed: field.min === null || field.min === undefined || field.min < 0,
      max: field.max ?? null,
      maxExclusive: !!field.max_exclusive,
    };
  }
  if (field.type !== "string") return null;
  if (field.check === "tax_id") return { kind: "tax_id", label: "БИН/ИИН" };
  if (field.check === "iik" || field.check === "bic" || field.check === "kbe") return { kind: field.check };
  if (PHONE_NAMES.has(field.name)) return { kind: "phone" };
  if (CODE_NAMES.test(field.name) || field.name === "comment" || field.width === "xl" || (field.max_length ?? 0) > 300) return { kind: "free" };
  return field.max_length ? { kind: "text", maxLength: field.max_length } : null;
}

/** The БИН / ИИН words of a tax identifier follow the form of the partner («ТОО» → БИН), as its label does. */
export function withTaxLabel(policy: InputPolicy | null, kind: unknown): InputPolicy | null {
  return policy?.kind === "tax_id" ? { ...policy, label: taxIdLabel(kind) } : policy;
}

/**
 * The policy of a field of an action dialog with the same meaning (UXW-66): its numbers are never negative;
 * QA0211-B02: a quantity in the unit of a product takes the digits of that unit (pieces — whole numbers).
 */
export function policyOfActionField(field: Pick<ActionField, "type">, unit?: { name: string; decimals: number } | null): InputPolicy | null {
  if (field.type !== "number") return null;
  return unit ? { kind: "number", signed: false, decimals: unit.decimals, unit: unit.name } : { kind: "number", signed: false };
}
